import i18n, { createInstance, type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type LanguageCode } from './config';

import pl from './locales/pl.json';
import en from './locales/en.json';

const resources = {
  pl: {
    translation: pl,
  },
  en: {
    translation: en,
  },
};

export function createI18nInstance(language: LanguageCode): I18nInstance {
  const instance = createInstance();
  instance.use(initReactI18next).init({
    resources,
    lng: language,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES.map(lang => lang.code),
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });
  return instance;
}

export function initI18nClient(language: LanguageCode): I18nInstance {
  if (!i18n.isInitialized) {
    i18n.use(initReactI18next).init({
      resources,
      lng: language,
      fallbackLng: 'en',
      supportedLngs: SUPPORTED_LANGUAGES.map(lang => lang.code),
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
      },
    });
  } else if (i18n.language !== language) {
    i18n.changeLanguage(language);
  }
  return i18n;
}

export function getSavedLanguage(): LanguageCode | null {
  if (typeof window === 'undefined') return null;
  try {
    const saved = window.localStorage.getItem('noteon_language');
    if (!saved) return null;
    const isSupported = SUPPORTED_LANGUAGES.some(lang => lang.code === saved);
    if (!isSupported) {
      window.localStorage.removeItem('noteon_language');
      return null;
    }
    return saved as LanguageCode;
  } catch {
    return null;
  }
}

export function setSavedLanguage(lang: LanguageCode) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem('noteon_language', lang);
  } catch {
    // Ignore storage errors
  }
}

// Function to change language (client only)
export function changeLanguage(
  lang: LanguageCode,
  options?: { persist?: boolean }
) {
  if (options?.persist !== false) {
    setSavedLanguage(lang);
  }
  i18n.changeLanguage(lang);
}

export function getDefaultLanguage(): LanguageCode {
  return DEFAULT_LANGUAGE;
}

export default i18n;
